import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { type PaginatedResponse } from '../../../api/client'
import {
  createContactApi,
  updateContactApi,
  patchContactApi,
  deleteContactApi,
  deleteContactsApi,
  createContactGroupApi,
  updateContactGroupApi,
  patchContactGroupApi,
  deleteContactGroupApi,
  deleteContactGroupsApi,
} from '../api/contactApi'
import { ContactGroup, type Contact } from '../../../utils/types'
import LogTool from '../../../logger/logTools'
import { contactGroupKeys, contactKeys } from '../utils/queryKeys'
import { customerKeys } from '../../Customer/utils/queryKeys'
import { supplierKeys } from '../../Supplier/utils/queryKeys'
import { getPKfromSelf } from '../../../utils/functions'

const log = new LogTool({ context: 'ContactGroupMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a new contact group
 */
export function useCreateContactGroup(): UseMutationResult<ContactGroup, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createContactGroupApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: contactGroupKeys.lists() })
      log.info('Contact group created successfully')
    },
    onError: error => {
      log.error('Error creating contact group:', error)
    },
  })
}

/**
 * Hook to update a contact (full update with PUT)
 */
export function useUpdateContactGroup(): UseMutationResult<
  ContactGroup,
  Error,
  { idOrSelf: string | number; data: any }
> {
  const queryClient = useQueryClient()

  const updateCachedContact = (updatedContactGroup: ContactGroup): void => {
    // update cached list responses so UI can use fresh data without waiting for a refetch
    queryClient.setQueriesData<PaginatedResponse<ContactGroup>>(
      { queryKey: contactKeys.lists() },
      oldData => {
        if (!oldData?.results) return oldData
        return {
          ...oldData,
          results: oldData.results.map(contact =>
            contact.self === updatedContactGroup.self ? updatedContactGroup : contact
          ),
        }
      }
    )

    // update any cached detail queries for the same contact (regardless of expand params)
    queryClient.setQueriesData<ContactGroup>({ queryKey: contactKeys.details() }, cachedContact =>
      cachedContact?.self === updatedContactGroup.self ? updatedContactGroup : cachedContact
    )
  }

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateContactGroupApi(idOrSelf, data),
    onSuccess: async updatedContact => {
      updateCachedContact(updatedContact)
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: contactGroupKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: contactGroupKeys.details() })
      log.info('Contact group updated successfully')
    },
    onError: error => {
      log.error('Error updating contact group:', error)
    },
  })
}

/**
 * Hook to patch a contact (partial update with PATCH)
 */
export function usePatchContactGroup(): UseMutationResult<
  ContactGroup,
  Error,
  { idOrSelf: string | number; data: Partial<any> }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => patchContactGroupApi(idOrSelf, data),
    onSuccess: async updatedContactGroup => {
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({
        queryKey: contactGroupKeys.detail(Number(updatedContactGroup.key)),
      })
      log.info('Contact group patched successfully')
    },
    onError: error => {
      log.error('Error patching contact group:', error)
    },
  })
}

/**
 * Hook to delete a single contact
 */
export function useDeleteContactGroup(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteContactGroupApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: contactGroupKeys.lists() })
      log.info('Contact group deleted successfully')
    },
    onError: error => {
      log.error('Error deleting contact group:', error)
    },
  })
}

/**
 * Hook to delete multiple contacts
 */
export function useDeleteContactGroups(): UseMutationResult<void, Error, ContactGroup[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteContactGroupsApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: contactGroupKeys.lists() })
      log.info('Contact groups deleted successfully')
    },
    onError: error => {
      log.error('Error deleting contact groups:', error)
    },
  })
}
